import { Box, ButtonBase, Drawer, IconButton, Radio, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusGroup } from "../helpers";

interface StatusFilterDrawerProps {
  open: boolean;
  value: StatusGroup | null;
  onClose: () => void;
  onApply: (group: StatusGroup | null) => void;
}

const OPTIONS: { code: StatusGroup; labelKey: string }[] = [
  { code: "success", labelKey: "history:statuses.succeeded" },
  { code: "processing", labelKey: "history:statuses.pending" },
  { code: "error", labelKey: "history:statuses.failed" },
];

export function StatusFilterDrawer({ open, value, onClose, onApply }: StatusFilterDrawerProps) {
  const { t } = useTranslation(["history", "common"]);
  const [selected, setSelected] = useState<StatusGroup | null>(value);

  useEffect(() => {
    if (open) setSelected(value);
  }, [open, value]);

  const apply = () => {
    onApply(selected);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } } }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 20px",
          backgroundImage: "none",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 4,
          borderRadius: 4,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {t("history:filters.status_drawer_title")}
        </Typography>
        <IconButton onClick={onClose} sx={{ padding: "6px" }}>
          <Icon icon="solar:close-circle-bold-duotone" width={26} height={26} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", display: "grid", gap: "14px" }}>
        <Row
          label={t("history:filters.select_all_statuses")}
          checked={selected === null}
          onClick={() => setSelected(null)}
        />

        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              marginBottom: "8px",
              paddingInline: "4px",
            }}
          >
            {t("history:filters.for_all_operations")}
          </Typography>
          <Box
            sx={(theme) => ({
              borderRadius: "18px",
              overflow: "hidden",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.04)",
            })}
          >
            {OPTIONS.map((opt, i) => (
              <Row
                key={opt.code}
                label={t(opt.labelKey)}
                checked={selected === opt.code}
                onClick={() => setSelected(opt.code)}
                divider={i < OPTIONS.length - 1}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <ButtonBase
        onClick={apply}
        sx={{
          marginTop: "16px",
          padding: "16px",
          borderRadius: "16px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontWeight: 700,
          fontSize: "15px",
          transition: "opacity 0.15s",
          "&:active": { opacity: 0.85 },
        }}
      >
        {t("common:actions.apply")}
      </ButtonBase>
    </Drawer>
  );
}

function Row({
  label,
  checked,
  onClick,
  divider = false,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
  divider?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 16px",
        textAlign: "left",
        borderRadius: divider ? 0 : "18px",
        borderBottom: divider
          ? `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)"
            }`
          : "none",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.04)",
      })}
    >
      <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>{label}</Typography>
      <Radio
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onClick}
        sx={{ padding: 0, color: "text.secondary" }}
      />
    </ButtonBase>
  );
}
